import React from 'react';

export interface AttitudeIndicatorProps {
  roll: number; // degrees, positive right wing down
  pitch: number; // degrees, positive nose up
  heading: number; // degrees, 0 = North
  windSpeed: number; // mph
  windDirection: number; // degrees
}

const OUTER_SIZE = 160; // px, overall SVG size
const COMPASS_RADIUS = OUTER_SIZE / 2 - 8; // compass rose ring
const ATTITUDE_RADIUS = OUTER_SIZE / 2 - 24; // inner attitude indicator
const PADDING = 12;
const CENTER = OUTER_SIZE / 2;
const pitchScale = 2; // px per degree, adjust for sensitivity

const AttitudeIndicator: React.FC<AttitudeIndicatorProps> = ({ roll, pitch, heading, windSpeed, windDirection }) => {
  // Limit pitch translation so the horizon doesn't disappear
  const maxPitch = 30; // degrees
  const clampedPitch = Math.max(Math.min(pitch, maxPitch), -maxPitch);

  return (
    <svg
      width={OUTER_SIZE + 2 * PADDING}
      height={OUTER_SIZE + 2 * PADDING}
      viewBox={`-${PADDING} -${PADDING} ${OUTER_SIZE + 2 * PADDING} ${OUTER_SIZE + 2 * PADDING}`}
      style={{ display: 'block', overflow: 'visible', border: 'solid 1px' }}
    >
      <defs>
        <linearGradient id='sky' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor='#6ec6ff' />
          <stop offset='100%' stopColor='#1565c0' />
        </linearGradient>
        <linearGradient id='ground' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor='#8d6e63' />
          <stop offset='100%' stopColor='#4e342e' />
        </linearGradient>
        <clipPath id='attitude-clip'>
          <circle cx={CENTER} cy={CENTER} r={ATTITUDE_RADIUS} />
        </clipPath>
      </defs>

      {/* Outer compass rose ring (rotates with heading) */}
      <g transform={`rotate(${-heading} ${CENTER} ${CENTER})`}>
        <circle cx={CENTER} cy={CENTER} r={COMPASS_RADIUS} fill='none' stroke='#bbb'
strokeWidth='0' />
        {[0, 90, 180, 270].map((deg, i) => {
          const label = ['N', 'E', 'S', 'W'][i];
          const angle = (deg * Math.PI) / 180;
          const rText = ATTITUDE_RADIUS + (COMPASS_RADIUS - ATTITUDE_RADIUS) * 0.72;
          // Main tick: start at minor tick inner radius, extend outward
          const rTickInner = COMPASS_RADIUS + 15;
          const rTick = COMPASS_RADIUS + 6;
          // Direction-based offsets for label positioning
          const dx = 0;
          let dy = 0;
          switch (deg) {
            case 0:
              dy = 6;
              break; // N, move up
            case 180:
              dy = 5;
              break; // S, move down
            case 90:
              dy = 5;
              break; // E, slight down
            case 270:
              dy = 5;
              break; // W, slight down
            default:
              dy = 0;
          }

          return (
            <g key={deg}>
              {/* Major tick (extends outward) */}
              <line
                x1={CENTER + rTickInner * Math.sin(angle)}
                y1={CENTER - rTickInner * Math.cos(angle)}
                x2={CENTER + rTick * Math.sin(angle)}
                y2={CENTER - rTick * Math.cos(angle)}
                stroke='#fff'
                strokeWidth='1.8'
                opacity={0.95}
              />
              {/* Main direction label (equally spaced) */}
              <text
                x={CENTER + rText * Math.sin(angle) + dx}
                y={CENTER - rText * Math.cos(angle) + dy}
                fontSize='16'
                fill='#ff0'
                textAnchor='middle'
                fontWeight='bold'
                style={{ textShadow: '0 0 4px #222' }}
              >
                {label}
              </text>
            </g>
          );
        })}
        {/* Minor ticks (every 15 deg, but thinner and more transparent) */}
        {Array.from({ length: 24 }, (_, i) => i * 15).map((deg) => {
          if (deg % 90 === 0) return null; // skip main ticks
          const angle = (deg * Math.PI) / 180;
          const rTick = COMPASS_RADIUS;
          const rTickInner = COMPASS_RADIUS - 5;
          return (
            <line
              key={deg}
              x1={CENTER + rTick * Math.sin(angle)}
              y1={CENTER - rTick * Math.cos(angle)}
              x2={CENTER + rTickInner * Math.sin(angle)}
              y2={CENTER - rTickInner * Math.cos(angle)}
              stroke='#fff'
              strokeWidth='1'
              opacity={0.5}
            />
          );
        })}
      </g>

      {/* Inner attitude indicator (pitch/roll), clipped to circle */}
      <g
        clipPath='url(#attitude-clip)'
        transform={`translate(${CENTER},${CENTER}) rotate(${roll}) translate(${-CENTER},${
          -CENTER + clampedPitch * pitchScale
        })`}
      >
        {/* Sky */}
        <rect
          x={CENTER - ATTITUDE_RADIUS}
          y={CENTER - ATTITUDE_RADIUS}
          width={ATTITUDE_RADIUS * 2}
          height={ATTITUDE_RADIUS}
          fill='url(#sky)'
        />
        {/* Ground */}
        <rect
          x={CENTER - ATTITUDE_RADIUS}
          y={CENTER}
          width={ATTITUDE_RADIUS * 2}
          height={ATTITUDE_RADIUS}
          fill='url(#ground)'
        />
        {/* Horizon line */}
        <line
          x1={CENTER - ATTITUDE_RADIUS}
          y1={CENTER}
          x2={CENTER + ATTITUDE_RADIUS}
          y2={CENTER}
          stroke='#fff'
          strokeWidth='2'
        />
        {/* Pitch ticks (every 10 deg, ±30 deg) */}
        {[-30, -20, -10, 0, 10, 20, 30].map((deg) => (
          <g key={deg} transform={`translate(0,${CENTER - deg * pitchScale})`} opacity={deg === 0 ? 1 : 0.6}>
            <line x1={CENTER - 24} x2={CENTER + 24} y1={0} y2={0} stroke='#fff'
strokeWidth={deg === 0 ? 2 : 1} />
            {deg !== 0 && (
              <text x={CENTER + 28} y={4} fontSize='11' fill='#fff' textAnchor='start'
fontWeight='bold'>
                {deg}
              </text>
            )}
          </g>
        ))}
      </g>

      {/* Attitude indicator circle border */}
      <circle cx={CENTER} cy={CENTER} r={ATTITUDE_RADIUS} fill='none' stroke='#fff'
strokeWidth='1' opacity={0.85} />

      {/* Central dark circle */}
      <circle cx={CENTER} cy={CENTER} r={18} fill='#222' stroke='#444'
strokeWidth='2' />
      {/* Wind arrow, centered and rotated */}
      <g transform={`rotate(${windDirection} ${CENTER} ${CENTER})`}>
        <polygon
          points={`
            ${CENTER},${CENTER - 12}
            ${CENTER + 7},${CENTER + 7}
            ${CENTER},${CENTER + 2}
            ${CENTER - 7},${CENTER + 7}
          `}
          fill='#ff0'
          stroke='#333'
          strokeWidth='1'
        />
      </g>
      {/* Wind speed label inside the circle */}
      <text x={CENTER} y={CENTER + 22} textAnchor='middle' fill='#fff' fontSize='12'
fontWeight='bold'>
        {windSpeed.toFixed(0)} mph
      </text>
    </svg>
  );
};

export default AttitudeIndicator;
